// REALISTIC AMBIENT ANIMALS (2026-11-04, owner: realistic models, commercial
// use, "modify enough that it is clearly our own"). The wildlife ambientLife.ts
// scatters over every map — birds overhead, critters on the ground, fish in
// the sea — now draws rigged Sketchfab animals (CC-BY 4.0, see assetCredits.ts
// and public/models/{crow,fish,hare}/CREDITS.md) instead of primitives.
//
// They stay ONE instanced draw call per species: three.js can't instance a
// skinned mesh, so agent/animals-asset/bake-morph.mjs baked one locomotion
// cycle of each rig into K morph-target frames (and made each animal ours:
// proportions, plumage/coat, cycle). Here a MorphHerd draws a whole flock as
// one InstancedMesh and blends two neighbouring frames per instance through
// InstancedMesh.setMorphAt — real animation at the cost of a few floats per
// animal per frame, no mixer, no skeleton, no allocation.
//
// Geometry is unit nose-to-tail length, forward −Z, up +Y (origin: body
// centre for birds/fish, feet for critters); the caller passes the length in
// metres. The cached GLB's geometry/material are tagged userData.shared
// (gltfAssets) so ambientLife's dispose walk leaves them alone; the herd's
// InstancedMesh owns its instance buffers + morph texture (freed by
// InstancedMesh.dispose()).
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { gltfIfReady, loadGltf, realModelsEnabled } from "./gltfAssets.js";

export const CROW_URL = "/models/crow/crow.glb";
export const FISH_URL = "/models/fish/fish.glb";
export const HARE_URL = "/models/hare/hare.glb";

/** The asset if it is already parsed and allowed on this machine (real models
 *  everywhere except the TV app; never outside a browser — node benches
 *  census the procedural build). */
export function ambientModelIfReady(url: string): GLTF | null {
  return realModelsEnabled() ? gltfIfReady(url) : null;
}

/** Start the fetch early; resolves null when disabled or unusable. */
export function preloadAmbientModel(url: string): Promise<GLTF | null> {
  if (!realModelsEnabled()) return Promise.resolve(null);
  void gltfIfReady(url); // arms the synchronous ready-peek
  return loadGltf(url);
}

const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);
const TMP_M = new THREE.Matrix4();
const TMP_S = new THREE.Matrix4();
const TMP_C = new THREE.Color();

/** One species drawn as one InstancedMesh with per-instance morph frames. */
export class MorphHerd {
  readonly im: THREE.InstancedMesh;
  /** Seconds the source cycle lasted (bake metadata). */
  readonly cycle: number;
  /** Frame index of the held glide pose (birds), 0 otherwise. */
  readonly glideFrame: number;
  private readonly frames: number;
  private readonly size: number;
  /** The mesh node's transform inside the GLB — meshopt QUANTIZES positions
   *  and morph targets and parks the de-quantization (scale + offset) on the
   *  node, so the raw geometry is not in metres until this is applied. */
  private readonly local = new THREE.Matrix4();
  /** setMorphAt reads `.morphTargetInfluences` off an object: one reusable
   *  carrier, never reallocated. */
  private readonly carrier: { morphTargetInfluences: number[] };

  /** Null when the GLB is not a baked morph herd (procedural stays). */
  static from(g: GLTF, cap: number, size: number, tints: readonly string[]): MorphHerd | null {
    let mesh: THREE.Mesh | null = null;
    g.scene.traverse((o) => { if (!mesh && (o as THREE.Mesh).isMesh) mesh = o as THREE.Mesh; });
    const m = mesh as THREE.Mesh | null;
    const frames = Number(m?.userData?.npFrames);
    if (!m || !(frames >= 2) || (m.geometry.morphAttributes.position?.length ?? 0) !== frames - 1) {
      console.error("[ambient] model is not a baked morph herd");
      return null;
    }
    return new MorphHerd(m, cap, size, tints);
  }

  private constructor(src: THREE.Mesh, cap: number, size: number, tints: readonly string[]) {
    this.frames = Number(src.userData.npFrames);
    this.cycle = Number(src.userData.npCycle) || 1;
    this.glideFrame = Number(src.userData.npGlide) || 0;
    this.size = size;
    src.updateWorldMatrix(true, false);
    this.local.copy(src.matrixWorld);
    this.carrier = { morphTargetInfluences: new Array<number>(this.frames - 1).fill(0) };
    const im = new THREE.InstancedMesh(src.geometry, src.material, cap);
    im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    im.frustumCulled = false; // one bound can't track a flock; one draw call over the map is cheap
    for (let i = 0; i < cap; i++) {
      im.setMatrixAt(i, ZERO); // every slot invisible until spawned
      im.setColorAt(i, TMP_C.set(tints[i % tints.length]));
      // allocates the morph texture now, so the instanced-morph program is
      // part of the load-screen compile (never a mid-match cold compile)
      im.setMorphAt(i, this.carrier as unknown as THREE.Mesh);
    }
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    if (im.morphTexture) im.morphTexture.needsUpdate = true;
    im.name = "npAmbientHerd";
    this.im = im;
  }

  /** Pose instance `slot` at cycle phase `phase` (any real; wraps), blended
   *  toward the held glide frame by `glide` (0..1). */
  setPose(slot: number, phase: number, glide = 0): void {
    const inf = this.carrier.morphTargetInfluences;
    inf.fill(0);
    const K = this.frames;
    const p = (phase - Math.floor(phase)) * K;
    const f0 = Math.floor(p) % K, f1 = (f0 + 1) % K, t = p - Math.floor(p);
    const flap = 1 - glide;
    // frame 0 is the base (all weights zero); frame f>0 is target f−1
    if (f0 > 0) inf[f0 - 1] += (1 - t) * flap;
    if (f1 > 0) inf[f1 - 1] += t * flap;
    if (glide > 0 && this.glideFrame > 0) inf[this.glideFrame - 1] += glide;
    this.im.setMorphAt(slot, this.carrier as unknown as THREE.Mesh);
  }

  /** Place instance `slot` with a body transform (the herd's unit geometry is
   *  scaled to `size` metres, times any scale already in `m`). */
  setMatrix(slot: number, m: THREE.Matrix4): void {
    TMP_M.multiplyMatrices(m, TMP_S.makeScale(this.size, this.size, this.size)).multiply(this.local);
    this.im.setMatrixAt(slot, TMP_M);
  }

  hide(slot: number): void { this.im.setMatrixAt(slot, ZERO); }

  /** Once per frame after the writes. */
  flush(): void {
    this.im.instanceMatrix.needsUpdate = true;
    if (this.im.morphTexture) this.im.morphTexture.needsUpdate = true;
  }
}
